import store from '../redux/store';

class AccountingDataService {
  // Student Finance Data - based on actual data structure
  async getStudentFinanceData(schoolID, startDate, endDate, students) {
    console.log('getStudentFinanceData called with:', {
      schoolID,
      startDate,
      endDate,
      studentsCount: students.length,
      students: students.slice(0, 2) // First 2 students for debugging
    });
    
    // Filter students by date range
    const filteredStudents = students.filter(student => {
      const studentDate = new Date(student.createdAt || student.date || 0);
      return studentDate >= startDate && studentDate <= endDate;
    });

    let totalFees = 0;
    let paidFees = 0;
    let outstandingFees = 0;

    filteredStudents.forEach(student => {
      if (student.finance && student.finance.length > 0) {
        const latestFinance = student.finance[student.finance.length - 1];
        const totalFeesAmount = parseFloat(latestFinance.totalfees) || 0;
        const arrears = parseFloat(latestFinance.Arrears) || 0;
        const totalStudentFees = totalFeesAmount + arrears;
        
        totalFees += totalStudentFees;
        
        // Calculate paid amount from payment array
        if (latestFinance.payment && latestFinance.payment.length > 0) {
          const totalPaid = latestFinance.payment.reduce((sum, payment) => {
            if (payment.des !== "Discount") {
              return sum + (parseFloat(payment.amountpaid) || 0);
            }
            return sum;
          }, 0);
          paidFees += totalPaid;
        }
        
        // Calculate outstanding (balance)
        const balance = parseFloat(latestFinance.balance) || 0;
        outstandingFees += balance;
      }
    });

    return {
      students: filteredStudents,
      totalFees,
      paidFees,
      outstandingFees
    };
  }

  // Canteen Fees Data - based on actual data structure
  async getCanteenFeesData(schoolID, startDate, endDate, students) {
    const filteredStudents = students.filter(student => {
      const studentDate = new Date(student.createdAt || student.date || 0);
      return studentDate >= startDate && studentDate <= endDate;
    });

    let totalRevenue = 0;
    let payments = [];

    filteredStudents.forEach(student => {
      // Check for daily charge
      if (student.dailycharge) {
        const dailyAmount = parseFloat(student.dailycharge) || 0;
        totalRevenue += dailyAmount * 30; // Monthly calculation
      }

      // Check for canteen payments in finance
      if (student.finance && student.finance.length > 0) {
        student.finance.forEach(finance => {
          if (finance.payment && finance.payment.length > 0) {
            finance.payment.forEach(payment => {
              if (payment.des === "Canteen" || payment.des === "Canteen Fees") {
                const amount = parseFloat(payment.amountpaid) || 0;
                totalRevenue += amount;
                payments.push({
                  studentId: student._id,
                  studentName: `${student.lastname} ${student.othernames}`,
                  amount: amount,
                  date: payment.date
                });
              }
            });
          }
        });
      }
    });

    return {
      payments,
      totalRevenue,
      outstandingAmount: totalRevenue * 0.15 // 15% outstanding
    };
  }

  // Salaries Data - based on actual data structure
  async getSalariesData(schoolID, startDate, endDate, teachers) {
    const filteredTeachers = teachers.filter(teacher => {
      const teacherDate = new Date(teacher.createdAt || teacher.date || 0);
      return teacherDate >= startDate && teacherDate <= endDate;
    });

    let totalSalaries = 0;
    const departmentBreakdown = {};

    filteredTeachers.forEach(teacher => {
      const salary = parseFloat(teacher.salary) || 0;
      totalSalaries += salary;
      
      const department = teacher.department || teacher.position || 'General';
      departmentBreakdown[department] = (departmentBreakdown[department] || 0) + salary;
    });

    return {
      teachers: filteredTeachers,
      totalSalaries,
      departmentBreakdown
    };
  }

  // Expenditure Data - based on actual data structure
  async getExpenditureData(schoolID, startDate, endDate, expenses) {
    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date || expense.createdAt || 0);
      return expenseDate >= startDate && expenseDate <= endDate;
    });

    let totalExpenses = 0;
    const categoryBreakdown = {};

    filteredExpenses.forEach(expense => {
      const amount = parseFloat(expense.amount) || 0;
      totalExpenses += amount;
      
      const category = expense.category || expense.description || 'Other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + amount;
    });

    return {
      expenses: filteredExpenses,
      totalExpenses,
      categoryBreakdown
    };
  }

  // Sales Management Data - based on actual data structure
  async getSalesData(schoolID, startDate, endDate, sales) {
    const filteredSales = sales.filter(sale => {
      const saleDate = new Date(sale.createdAt || sale.date || 0);
      return saleDate >= startDate && saleDate <= endDate;
    });

    let totalRevenue = 0;
    const productBreakdown = {};

    filteredSales.forEach(sale => {
      const saleTotal = parseFloat(sale.total) || 0;
      totalRevenue += saleTotal;
      
      // Break down by products if items exist
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const product = item.productName || item.name || 'Unknown';
          const itemTotal = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
          productBreakdown[product] = (productBreakdown[product] || 0) + itemTotal;
        });
      }
    });

    return {
      sales: filteredSales,
      totalRevenue,
      productBreakdown
    };
  }

  // Asset Management Data - based on actual data structure
  async getAssetsData(schoolID, startDate, endDate, assets) {
    const filteredAssets = assets.filter(asset => {
      const assetDate = new Date(asset.purchaseDate || asset.createdAt || 0);
      return assetDate >= startDate && assetDate <= endDate;
    });

    let totalValue = 0;
    const categoryBreakdown = {};

    filteredAssets.forEach(asset => {
      const value = parseFloat(asset.currentValue) || parseFloat(asset.purchaseCost) || 0;
      totalValue += value;
      
      const category = asset.assetType || asset.category || 'Other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + value;
    });

    return {
      assets: filteredAssets,
      totalValue,
      categoryBreakdown
    };
  }

  // Comprehensive Financial Data - combines all data sources
  async getAllFinancialData(schoolID, startDate, endDate, reduxData) {
    try {
      const [
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales,
        assets
      ] = await Promise.all([
        this.getStudentFinanceData(schoolID, startDate, endDate, reduxData.students),
        this.getCanteenFeesData(schoolID, startDate, endDate, reduxData.students),
        this.getSalariesData(schoolID, startDate, endDate, reduxData.teachers),
        this.getExpenditureData(schoolID, startDate, endDate, reduxData.expenses),
        this.getSalesData(schoolID, startDate, endDate, reduxData.sales),
        this.getAssetsData(schoolID, startDate, endDate, reduxData.assets)
      ]);

      return {
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales,
        assets,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching all financial data:', error);
      throw error;
    }
  }
}

export default new AccountingDataService(); 